import { Button, Checkbox, Clickbox, Icon, Input, ISelectOption, MessageHelper, Select } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { useRef } from 'react';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { IAction, ISurvey, SurveyLogoPlacement } from '../../../models';
import { ISurveyImageState } from '../../../store/SurveyImageSlice';
import { ErrorMessageLabelNoIcon } from '../../audience/SharedComponents';
import { Col3Col9 } from '../../common';

export const SurveyLogoContainer = styled('div')`
	display: flex;
	justify-content: space-between;
`;
SurveyLogoContainer.displayName = 'SurveyLogoContainer';

const SurveyLogoImageContainer = styled.div.attrs({
	className: 'tws-survey-logo-image-container',
})`
	width: 120px;
	height: 120px;
	border: 1px solid #8c8c8c;
	border-right: 0;
	border-radius: 3px 0 0 3px;
	display: flex;
	align-items: center;
	justify-content: center;

	> img {
		max-width: 118px;
		max-height: 118px;
	}
`;
SurveyLogoImageContainer.displayName = 'SurveyLogoImageContainer';

const SurveyLogoNoImageContainer = styled(Clickbox).attrs({
	className: 'tws-survey-logo-no-image-container',
})`
	width: 120px !important;
	height: 120px !important;
`;
SurveyLogoNoImageContainer.displayName = 'SurveyLogoNoImageContainer';

const SurveyLogoIconsContainer = styled.div.attrs({
	className: 'btn-group-vertical tws-survey-logo-icons-container',
})`
	width: 40px;
	height: 120px;

	> .btn:first-child {
		border-top-left-radius: 0;
	}

	> .btn:last-child {
		border-top-left-radius: 0;
		border-top-right-radius: 0;
		border-bottom-left-radius: 0;
	}
`;
SurveyLogoIconsContainer.displayName = 'SurveyLogoIconsContainer';

const SurveyLogoIconColumn = styled(Button).attrs({
	className: 'tws-survey-logo-upload-icon-column',
	floating: true,
})``;
SurveyLogoIconColumn.displayName = 'SurveyLogoIconColumn';

const InputFile = styled.input.attrs({
	type: 'file',
	className: 'tws-input-file-upload',
})`
	display: none;
`;
InputFile.displayName = 'InputFile';

type SurveyLogoSectionProps = {
	disabled?: boolean;
	onDisplayNameChange: (displayName: string) => void;
	onLogoChange: (file: File) => void;
	onLogoRemoved: () => void;
	onPlacementChange: (placement: SurveyLogoPlacement) => void;
	onShowKincentricLogoChange: (showKincentricLogo: boolean) => void;
} & Pick<ISurveyImageState, 'surveyImage' | 'file' | 'validationResult'> &
	Pick<ISurvey, 'showKincentricLogo'>;

const surveyLogoPlacementOptions: ISelectOption[] = [
	{ displayText: SurveyLogoPlacement[SurveyLogoPlacement.Left], name: SurveyLogoPlacement.Left.toString() },
	{ displayText: SurveyLogoPlacement[SurveyLogoPlacement.Center], name: SurveyLogoPlacement.Center.toString() },
];

const SurveyLogoSection = ({
	disabled,
	file,
	surveyImage,
	showKincentricLogo,
	validationResult,
	onDisplayNameChange,
	onLogoChange,
	onLogoRemoved: handleLogoRemoved,
	onPlacementChange,
	onShowKincentricLogoChange,
}: SurveyLogoSectionProps) => {
	const inputFileUpload = useRef<HTMLInputElement>(null);

	const handleUploadFileClick = () => inputFileUpload.current?.click();

	const handleDisplayNameChange = (changeEvent: React.ChangeEvent<HTMLInputElement>) => onDisplayNameChange(changeEvent.target.value);

	const handlePlacementChange = (value: string | string[] | undefined) => {
		if (typeof value !== 'string') {
			return;
		}

		onPlacementChange(+value);
	};

	const handleFileChange = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
		const { target: input } = changeEvent;

		if (input.files === null || input.files.length === 0) {
			return;
		}

		onLogoChange(input.files[0]);
		onDisplayNameChange(getFileName(input.files[0].name));
		input.value = '';
	};

	const handleShowKincentricLogoChange = (changeEvent: React.ChangeEvent<HTMLInputElement>) =>
		onShowKincentricLogoChange(changeEvent.target.checked);

	const getFileName = (filePath: string) => {
		let fileName = filePath.substring(filePath.lastIndexOf('\\') + 1).split('.')[0];
		if (fileName.length > 50) {
			fileName = fileName.substring(0, 50);
		}
		return fileName;
	};

	return (
		<>
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_LOGO}>
				<SurveyLogoContainer className={'survey-logo-container'}>
					<div>
						{ContentConstants.LABEL_SURVEY_LOGO}
						<ul style={{ padding: '15px' }}>
							<li>{ContentConstants.LABEL_SURVEY_LOGO_MIME_TYPE}</li>
							<li>{ContentConstants.LABEL_SURVEY_LOGO_FILE_SIZE}</li>
							<li>{ContentConstants.LABEL_SURVEY_LOGO_BACKGROUND}</li>
							<li>{ContentConstants.LABEL_SURVEY_LOGO_DIMENSION}</li>
						</ul>
					</div>
					{(surveyImage.surveyImageId && surveyImage.editState !== IAction.Delete) || file ? (
						<div style={{ display: 'flex' }}>
							<SurveyLogoImageContainer>
								{surveyImage.image && !file && <img alt={surveyImage.displayName} src={`data:image/png;base64,${surveyImage.image}`} />}
								{!surveyImage.image && file && <img alt={ContentConstants.SUBSECTIONHEADERS_SURVEY_LOGO} src={URL.createObjectURL(file)} />}
							</SurveyLogoImageContainer>
							<SurveyLogoIconsContainer>
								<SurveyLogoIconColumn aria-label='Choose a new client logo' disabled={disabled} onClick={handleUploadFileClick}>
									<Icon name='upload' />
								</SurveyLogoIconColumn>
								<SurveyLogoIconColumn aria-label='Remove the client logo' disabled={disabled} onClick={handleLogoRemoved}>
									<Icon name='delete' />
								</SurveyLogoIconColumn>
							</SurveyLogoIconsContainer>
						</div>
					) : (
						<div>
							<SurveyLogoNoImageContainer aria-label='Choose a client logo' disabled={disabled} onClick={handleUploadFileClick}>
								<Icon name='add' />
							</SurveyLogoNoImageContainer>
						</div>
					)}
				</SurveyLogoContainer>
				{!!validationResult.failures.file?.length && (
					<ErrorMessageLabelNoIcon className='tws-survey-logo-upload-error-message'>
						{MessageHelper.getFormattedErrorMessages(validationResult.failures.file)}
					</ErrorMessageLabelNoIcon>
				)}
				<InputFile
					data-testid='survey-logo-file-input'
					disabled={disabled}
					ref={inputFileUpload}
					accept={'image/png, image/gif, image/jpeg, image/bmp'}
					onChange={handleFileChange}
				/>
			</Col3Col9>
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_LOGO_NAME}>
				<p>{ContentConstants.LABEL_SURVEY_LOGO_NAME}</p>
				<Input
					disabled={(!surveyImage.image && !file) || disabled}
					value={surveyImage.displayName}
					maxLength={50}
					placeholder={ContentConstants.SUBSECTIONHEADERS_SURVEY_LOGO_NAME}
					error={!!validationResult.failures.displayName?.length}
					message={validationResult.failures.displayName}
					onChange={handleDisplayNameChange}
				/>
			</Col3Col9>
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_LOGO_PLACEMENT}>
				<Select
					className='tws-survey-logo-placement'
					selectSize='sm'
					disabled={(!surveyImage.image && !file) || disabled}
					displayText={ContentConstants.SUBSECTIONHEADERS_SURVEY_LOGO_PLACEMENT}
					items={surveyLogoPlacementOptions}
					selectedValue={surveyImage.placement.toString()}
					onApply={handlePlacementChange}
				/>
			</Col3Col9>
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_KINCENTRIC_LOGO}>
				<Checkbox
					aria-label='Show Kincentric logo on survey.'
					disabled={disabled}
					checked={showKincentricLogo}
					onChange={handleShowKincentricLogoChange}
				/>
			</Col3Col9>
		</>
	);
};

export { SurveyLogoSection, type SurveyLogoSectionProps };
